/**
 * How a career reads once it is over.
 *
 * The four tiers, their headers and their narrative sentences are verbatim from
 * `PS.EXE`'s `simend.c` strings, transcribed in docs/game/campaign.md
 * § *End-of-career outcome tiers* — including the stunt-pilot and crop-duster
 * endings, which are the game's own words and not a flourish of ours. The
 * Ace-of-Aces bar is the manual's: beat René Fonck's 75 as an Allied pilot or
 * the Red Baron's 80 as a German one.
 *
 * The three closing lines for the three ways out — armistice, killed in action,
 * grounded for misconduct — are likewise verbatim `PS.EXE` templates, with
 * their `%n` / `%q` / `%d` slots filled.
 *
 * **The one port-side decision** is what makes a pilot a "ranking ace": the
 * `[N] RANKING ACE!` header needs an N, and rather than invent a table of
 * historical scores to be placed against, this uses the game's own VIEW TOP
 * ACES list — the top ten careers by victories that this installation has
 * recorded (`leaderboard.ts`). A career that lands in that ten is a ranking
 * ace, and N is its place in it.
 */

import type { Nation } from "../mission/mission-roster.ts";
import { formatCareerDate, formatCareerMonth } from "./calendar.ts";
import { squadronOf, totalVictories, type CareerState } from "./career-state.ts";

export const CAREER_TIERS = ["aceOfAces", "rankingAce", "ace", "ordinary"] as const;

export type CareerTier = (typeof CAREER_TIERS)[number];

/** Beat these and you are the war's top ace (manual, "Career Play"). */
export const NATIONAL_TOP_SCORE: Readonly<Record<Nation, number>> = { allied: 75, german: 80 };

/** Five victories has meant "ace" since 1915. */
export const ACE_VICTORIES = 5;

const POSSESSIVE: Readonly<Record<Nation, string>> = { allied: "Britain", german: "Germany" };

export interface CareerOutcome {
  readonly tier: CareerTier;
  /** The screen's header. The bottom tier has none, in the original too. */
  readonly header: string | undefined;
  readonly narrative: string;
  /** Place on the top-aces list, when the pilot made it. */
  readonly ranking: number | undefined;
}

export interface OutcomeOptions {
  /** 1-based place on VIEW TOP ACES, if this career made the top ten. */
  readonly ranking?: number;
}

export function careerOutcome(career: CareerState, options: OutcomeOptions = {}): CareerOutcome {
  const victories = totalVictories(career);
  const nation = POSSESSIVE[career.nation];
  const ranking = options.ranking;
  if (victories > NATIONAL_TOP_SCORE[career.nation]) {
    return {
      tier: "aceOfAces",
      header: "ACE OF ACES!",
      narrative: `You finish the war as ${nation}'s top ace.`,
      ranking,
    };
  }
  if (ranking !== undefined && victories >= ACE_VICTORIES) {
    return {
      tier: "rankingAce",
      header: `${ranking} RANKING ACE!`,
      narrative: `You finish the war as one of ${nation}'s highest scoring aces.`,
      ranking,
    };
  }
  if (victories >= ACE_VICTORIES) {
    return {
      tier: "ace",
      header: "ACE",
      narrative: `You finish the war as one of ${nation}'s respected aces. After the war, you put your flying skill to use and tour the world as a stunt pilot as the Great ${career.name}!`,
      ranking: undefined,
    };
  }
  return {
    tier: "ordinary",
    header: undefined,
    narrative:
      "You finish the war as just another anonymous pilot. After the war, you put your flying skills to use and manage to make a living as a crop duster.",
    ranking: undefined,
  };
}

/**
 * The closing line on the pilot record, in `PS.EXE`'s own words for each of the
 * three ways a career ends. A career still in progress gets the plain
 * where-he-is-now line instead.
 */
export function careerEpitaph(career: CareerState): string {
  const unit = squadronOf(career).name;
  if (career.status === "dead") return `${career.name} was killed in action in ${formatCareerMonth(career.date)}.`;
  if (career.status === "discharged") {
    return `In ${formatCareerMonth(career.date)}, ${career.name} was grounded due to his flagrant disregard for military discipline.`;
  }
  if (career.status === "retired") {
    return `At the end of the war in Nov. 1918, ${career.name} was flying with ${unit}.`;
  }
  return `As of ${formatCareerDate(career.date)}, ${career.name} is flying with ${unit}.`;
}

/** The BOARD OF INQUIRY death notice, verbatim `PS.EXE`. */
export function killedInActionNotice(career: CareerState): readonly string[] {
  const unit = squadronOf(career).name;
  return [
    `${career.name} was killed in action today. ${unit} mourns his loss.`,
    `${career.name} killed in action in ${formatCareerMonth(career.date)}.`,
    `All of ${unit} mourns your death.`,
  ];
}
